import React, { useState } from "react";
import { useSelector } from "react-redux";
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Card, 
  CardContent,
  Stack,
  LinearProgress
} from "@mui/material";
import PeopleIcon from '@mui/icons-material/People';
import TokenIcon from '@mui/icons-material/Token';
import LandscapeIcon from '@mui/icons-material/Landscape';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { AppState } from "../../store";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';

type DateTimeFields = {
  regStart: string;
  regEnd: string;
  start: string;
  end: string;
};
type EpochFields = {
  regStart: number | null;
  regEnd: number | null;
  start: number | null;
  end: number | null;
};

const DashboardPanel: React.FC = () => {
  const userModule = useSelector((state: AppState) => state.userModule);
  const { gameStatus, loginInfo } = userModule;
  const navigate = useNavigate();
  
  // Calculate statistics
  const totalAssets = loginInfo.assets?.length || 0;
  const toolsCount = loginInfo.assets?.filter((asset: any) => asset.tool_config).length || 0;
  const deerCount = loginInfo.assets?.filter((asset: any) => !asset.tool_config).length || 0;
  const stakingPoolCount = gameStatus.pool_info?.length || 0;

  // Create data for the charts
  const tokenDistribution = [
    { name: 'WAX', value: parseFloat(loginInfo.balance.wax_balance) || 0 },
    { name: 'GOLD', value: parseFloat(loginInfo.balance.gold_balance) || 0 },
    { name: 'GRASS', value: parseFloat(loginInfo.balance.grass_balance) || 0 },
    { name: 'ANTLER', value: parseFloat(loginInfo.balance.antler_balance) || 0 }
  ];

  const assetDistribution = [
    { name: 'Tools', value: toolsCount },
    { name: 'Deer', value: deerCount }
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#D62728', '#9467BD'];

  // Format to show only the unique users from the leaderboard
  const uniqueUsers = new Set(gameStatus.leaderboard?.map((entry: any) => entry.username));
  const activeUsers = uniqueUsers.size;

  // Tournament data
  const tournamentCount = gameStatus.tournaments?.length || 0;
  const activeTournaments = gameStatus.tournaments?.filter((t: any) => !t.completed).length || 0;
  const completedTournaments = tournamentCount - activeTournaments;

  // World data
  const openWorlds = loginInfo.open_worlds?.length || 0;
  const totalWorlds = gameStatus.world_cost?.length || 0;

  // Generate some sample stats for demonstration
  const recentActivity = [
    { time: '2 mins ago', action: 'User staked 3 tools', user: 'user1.wam' },
    { time: '15 mins ago', action: 'Tournament #12 completed', user: 'system' },
    { time: '1 hour ago', action: 'New world unlocked', user: 'user2.wam' },
    { time: '3 hours ago', action: 'User claimed rewards', user: 'user3.wam' }
  ];

  const [dateTimes, setDateTimes] = useState<DateTimeFields>({
    regStart: "",
    regEnd: "",
    start: "",
    end: "",
  });
  const [epochs, setEpochs] = useState<EpochFields>({
    regStart: null,
    regEnd: null,
    start: null,
    end: null,
  });

  // Converts date-time string to epoch (seconds)
  const handleDateTimeChange = (field: keyof DateTimeFields, value: string) => {
    setDateTimes(prev => ({ ...prev, [field]: value }));
    const epoch = value ? Math.floor(new Date(value).getTime() / 1000) : null;
    setEpochs(prev => ({ ...prev, [field]: epoch }));
  };

  return (
    <Box sx={{ overflowX: 'hidden', width: '100vw', maxWidth: '100%', boxSizing: 'border-box' }}>
      <Typography variant="h5" sx={{ mb: 3, color: 'white' }}>Admin Dashboard</Typography>
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Users Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 140,
              bgcolor: 'rgba(25, 118, 210, 0.9)',
              color: 'white',
              borderRadius: 2
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography component="h2" variant="h6" gutterBottom>
                Active Users
              </Typography>
              <PeopleIcon />
            </Box>
            <Typography component="p" variant="h4">
              {activeUsers}
            </Typography>
            <Typography variant="body2" sx={{ mt: 'auto' }}>
              From leaderboard data
            </Typography>
          </Paper>
        </Grid>
        
        {/* Assets Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 140,
              bgcolor: 'rgba(46, 125, 50, 0.9)',
              color: 'white',
              borderRadius: 2
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography component="h2" variant="h6" gutterBottom>
                Total Assets
              </Typography>
              <TokenIcon />
            </Box>
            <Typography component="p" variant="h4">
              {totalAssets}
            </Typography>
            <Typography variant="body2" sx={{ mt: 'auto' }}>
              {toolsCount} Tools / {deerCount} Deer
            </Typography>
          </Paper>
        </Grid>
        
        {/* Worlds Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 140,
              bgcolor: 'rgba(237, 108, 2, 0.9)',
              color: 'white',
              borderRadius: 2
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography component="h2" variant="h6" gutterBottom>
                Worlds
              </Typography>
              <LandscapeIcon />
            </Box>
            <Typography component="p" variant="h4">
              {openWorlds} / {totalWorlds}
            </Typography>
            <Typography variant="body2" sx={{ mt: 'auto' }}>
              Open / Total Worlds
            </Typography>
          </Paper>
        </Grid>
        
        {/* Tournaments Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              height: 140,
              bgcolor: 'rgba(211, 47, 47, 0.9)',
              color: 'white',
              borderRadius: 2
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography component="h2" variant="h6" gutterBottom>
                Tournaments
              </Typography>
              <EmojiEventsIcon />
            </Box>
            <Typography component="p" variant="h4">
              {tournamentCount}
            </Typography>
            <Typography variant="body2" sx={{ mt: 'auto' }}>
              {activeTournaments} Active / {completedTournaments} Completed
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Charts and Create Tournament Side by Side */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Token Distribution Chart */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              borderRadius: 2,
              height: { xs: 300, sm: 360 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>Token Distribution</Typography>
            <ResponsiveContainer width="100%" height="80%">
              <PieChart margin={{ left: 0, right: 0 }}>
                <Pie
                  data={tokenDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={90}
                  innerRadius={50}
                  fill="#1976d2"
                  dataKey="value"
                  label={({ name }) => name}
                >
                  {tokenDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number, name: string, props: any) => [`${value}`, name]} contentStyle={{ backgroundColor: '#222', border: 'none', color: '#fff' }} />
                <Legend iconType="circle" wrapperStyle={{ color: '#fff', fontSize: 14 }} formatter={(value: string, entry: any, index: number) => {
                  const token = tokenDistribution.find(t => t.name === value);
                  return `${value}: ${token ? token.value : 0}`;
                }} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        {/* Asset Distribution Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.7)', color: 'white', borderRadius: 2, height: { xs: 300, sm: 360 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Asset Distribution</Typography>
            <ResponsiveContainer width="100%" height="80%">
              <BarChart
                data={assetDistribution}
                layout="vertical"
                margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                barCategoryGap={30}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                <XAxis type="number" stroke="#fff" hide={false} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" stroke="#fff" axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ backgroundColor: '#222', border: 'none', color: '#fff' }} />
                <Legend wrapperStyle={{ color: '#fff' }} />
                <Bar dataKey="value" name="Count" fill="#00C49F" radius={[8, 8, 8, 8]} label={{ position: 'right', fill: '#fff', fontWeight: 600 }} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        {/* Create Tournaments Section */}
        {/* <Grid item xs={12} md={6}>
          <Paper sx={{ p: 5, bgcolor: 'rgba(33, 33, 33, 0.9)', color: 'white', borderRadius: 2, maxWidth: 500, mx: { xs: 'auto', md: 0 } }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'left' }}>Create Tournament</Typography> */}
            {/* <form autoComplete="off">
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <input
                    type="number"
                    name="maxPlayers"
                    min={1}
                    required
                    placeholder="Max Players"
                    style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #555', background: '#222', color: 'white', textAlign: 'left' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <input
                    type="number"
                    name="minLevel"
                    min={1}
                    required
                    placeholder="Min Level"
                    style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #555', background: '#222', color: 'white', textAlign: 'left' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <input
                    type="number"
                    name="maxLevel"
                    min={1}
                    required
                    placeholder="Max Level"
                    style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #555', background: '#222', color: 'white', textAlign: 'left' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <select
                    name="schema"
                    required
                    defaultValue=""
                    style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #555', background: '#222', color: 'white', textAlign: 'left' }}
                  >
                    <option value="" disabled>Schema</option>
                    <option value="og.deer">og.deer</option>
                    <option value="fawn">fawn</option>
                    <option value="abyssal">abyssal</option>
                  </select>
                </Grid>
                <Grid item xs={12}>
                  <input
                    type="text"
                    name="tournamentId"
                    required
                    placeholder="Tournament ID"
                    style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #555', background: '#222', color: 'white', textAlign: 'left' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <label style={{ color: 'white', fontWeight: 500 }}>
                    Registration Start Date & Time
                    <input
                      type="datetime-local"
                      name="regStart"
                      value={dateTimes.regStart}
                      onChange={e => handleDateTimeChange('regStart', e.target.value)}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #555', background: '#222', color: 'white', marginTop: 4 }}
                    />
                    {dateTimes.regStart && (
                      <span style={{ marginLeft: 8, color: '#90caf9', fontSize: 13 }}>
                        (Epoch: {epochs.regStart})
                      </span>
                    )}
                  </label>
                </Grid>
                <Grid item xs={12}>
                  <label style={{ color: 'white', fontWeight: 500 }}>
                    Registration End Date & Time
                    <input
                      type="datetime-local"
                      name="regEnd"
                      value={dateTimes.regEnd}
                      onChange={e => handleDateTimeChange('regEnd', e.target.value)}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #555', background: '#222', color: 'white', marginTop: 4 }}
                    />
                    {dateTimes.regEnd && (
                      <span style={{ marginLeft: 8, color: '#90caf9', fontSize: 13 }}>
                        (Epoch: {epochs.regEnd})
                      </span>
                    )}
                  </label>
                </Grid>
                <Grid item xs={12}>
                  <label style={{ color: 'white', fontWeight: 500 }}>
                    Tournament Start Date & Time
                    <input
                      type="datetime-local"
                      name="start"
                      value={dateTimes.start}
                      onChange={e => handleDateTimeChange('start', e.target.value)}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #555', background: '#222', color: 'white', marginTop: 4 }}
                    />
                    {dateTimes.start && (
                      <span style={{ marginLeft: 8, color: '#90caf9', fontSize: 13 }}>
                        (Epoch: {epochs.start})
                      </span>
                    )}
                  </label>
                </Grid>
                <Grid item xs={12}>
                  <label style={{ color: 'white', fontWeight: 500 }}>
                    Tournament End Date & Time
                    <input
                      type="datetime-local"
                      name="end"
                      value={dateTimes.end}
                      onChange={e => handleDateTimeChange('end', e.target.value)}
                      style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #555', background: '#222', color: 'white', marginTop: 4 }}
                    />
                    {dateTimes.end && (
                      <span style={{ marginLeft: 8, color: '#90caf9', fontSize: 13 }}>
                        (Epoch: {epochs.end})
                      </span>
                    )}
                  </label>
                </Grid>
                <Grid item xs={12}>
                  <button
                    type="submit"
                    style={{ width: '100%', padding: 12, borderRadius: 4, background: 'linear-gradient(90deg, #1976d2 60%, #1565c0 100%)', color: 'white', border: 'none', fontWeight: 'bold', fontSize: 16, letterSpacing: 1 }}
                  >
                    Create Tournament
                  </button>
                </Grid>
              </Grid>
            </form> */}
          {/* </Paper>
        </Grid> */}
      </Grid>
    </Box>
  );
}

export default DashboardPanel;